import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent, SlideshowSelectEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

/**
 * A deferred media element
 * @typedef {Object} Refs
 * @property {HTMLElement} deferredMediaPlayButton - The button to show the deferred media content
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 * @property {HTMLElement} muteMediaButton - The button to toggle mute/unmute
 *
 * @extends {Component<Refs>}
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  /** @type {boolean} */
  isMuted = true;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;
    // If we're to use deferred media for images, we will need to run this only when it's not an image type media
    document.addEventListener(ThemeEvents.mediaStartedPlaying, this.pauseMedia.bind(this), {
      signal,
    });
    window.addEventListener(DialogCloseEvent.eventName, this.pauseMedia.bind(this), { signal });

    // Listen for slideshow slide changes to auto-play/pause video
    const slideshowComponent = this.closest('slideshow-component');
    if (slideshowComponent) {
      slideshowComponent.addEventListener(
        SlideshowSelectEvent.eventName,
        this.#handleSlideshowSelect,
        { signal }
      );

      // If this video's slide is already the active slide on load, auto-play it.
      if (this.localName === 'deferred-media') {
        requestAnimationFrame(() => {
          const slide = this.closest('slideshow-slide');
          if (slide?.getAttribute('aria-hidden') === 'false') {
            this.#startPlayback();
          }
        });
      }
    }

    // Attach ended listener to any already-rendered video element (autoplay case)
    this.#attachVideoEndedListener();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Attaches an 'ended' event listener to the <video> element if present,
   * or to YouTube/Vimeo iframes via postMessage API.
   */
  #attachVideoEndedListener() {
    const signal = this.#abortController.signal;
    const video = this.querySelector('video');
    if (video) {
      video.addEventListener('ended', this.#handleVideoEnded, { signal });
      return;
    }

    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      this.#attachIframeEndedListener(iframe, signal);
    }
  }

  /**
   * Attaches a window message listener for YouTube/Vimeo iframe ended events.
   * YouTube sends `{ event: 'onStateChange', info: 0 }` when playback ends.
   * Vimeo sends `{ event: 'finish' }` when playback ends.
   * After the iframe loads, sends subscription postMessages so the iframe
   * starts broadcasting state-change events back to the parent.
   * @param {HTMLIFrameElement} iframe
   * @param {AbortSignal} signal
   */
  #attachIframeEndedListener(iframe, signal) {
    const videoType = iframe.dataset.videoType;

    window.addEventListener(
      'message',
      (event) => {
        if (!iframe.contentWindow || event.source !== iframe.contentWindow) return;

        /** @type {any} */
        let data;
        try {
          data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        } catch {
          return;
        }

        if (videoType === 'youtube' && data.event === 'onStateChange' && data.info === 0) {
          this.#handleVideoEnded();
        } else if (videoType === 'vimeo' && data.event === 'finish') {
          this.#handleVideoEnded();
        }
      },
      { signal }
    );

    /**
     * Sends the subscription postMessage so the iframe starts broadcasting
     * state-change / finish events. Called once the iframe is loaded.
     */
    const subscribe = () => {
      if (!iframe.contentWindow) return;
      if (videoType === 'youtube') {
        iframe.contentWindow.postMessage(JSON.stringify({ event: 'listening' }), '*');
      } else if (videoType === 'vimeo') {
        iframe.contentWindow.postMessage(
          JSON.stringify({ method: 'addEventListener', value: 'finish' }),
          '*'
        );
      }
    };

    // If the iframe is already loaded (e.g. content was loaded before this
    // listener was attached during a reconnect), subscribe immediately.
    // Also listen for future load events in case the iframe hasn't loaded yet.
    if (iframe.getAttribute('data-media-loaded') || iframe.closest('[data-media-loaded]')) {
      subscribe();
    }
    iframe.addEventListener('load', subscribe, { signal });
  }

  /**
   * Handles the video 'ended' event — resets play state and shows the play button persistently
   */
  #handleVideoEnded = () => {
    this.isPlaying = false;
    this.updatePlayPauseHint(false);
    this.#updateMuteButtonVisibility(false);
    this.#setThumbnailPlayBadge(false);
  };

  /**
   * Handles slideshow slide changes: auto-plays the video when its slide becomes
   * active and pauses it when navigating away. Pausing also applies to 3D models,
   * but only videos are auto-started.
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    const { index, previousIndex, slide } = event.detail;

    // Ignore selects that aren't an actual slide change. A plain click/tap on the
    // current slide makes the slideshow dispatch a 'drag' select with
    // index === previousIndex; re-playing here would fight the click's own
    // play/pause toggle and leave the video paused after a "play" click.
    if (index === previousIndex) return;

    const isMySlide = slide?.contains(this);

    if (isMySlide) {
      // Only auto-play actual videos, never 3D models (<product-model>)
      if (this.localName === 'deferred-media') {
        this.#startPlayback();
      }
    } else if (this.isPlaying) {
      this.pauseMedia();
    }
  };

  /**
   * Starts playback, loading the deferred content first if it hasn't been loaded yet.
   */
  #startPlayback() {
    if (this.getAttribute('data-media-loaded')) {
      this.playMedia();
    } else {
      this.showDeferredMedia();
    }
  }

  /**
   * Hides this video's play badge on its gallery thumbnail while it is actively
   * playing, and restores it when paused or stopped.
   * @param {boolean} playing - Whether the video is currently playing
   */
  #setThumbnailPlayBadge(playing) {
    const gallery = this.closest('media-gallery');
    const slide = this.closest('slideshow-slide');
    if (!gallery || !slide) return;

    const index = Array.from(gallery.querySelectorAll('slideshow-slide')).indexOf(slide);
    if (index < 0) return;

    gallery.querySelectorAll('.slideshow-controls__thumbnails').forEach((container) => {
      const badge = container.children[index]?.querySelector(
        '.slideshow-controls__thumbnail-badge--play'
      );
      badge?.classList.toggle('slideshow-controls__thumbnail-badge--hidden', playing);
    });
  }

  /**
   * Updates mute button visibility based on playing state
   * @param {boolean} visible - Whether the mute button should be persistently visible
   */
  #updateMuteButtonVisibility(visible) {
    const muteButton = this.refs.muteMediaButton;
    if (muteButton instanceof HTMLElement) {
      muteButton.classList.toggle('video-mute-control--visible', visible);
    }
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);

      // Show play button persistently when not playing, hover-only when playing
      toggleMediaButton.classList.toggle('video-interaction-hint--visible', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
    this.#updateMuteButtonVisibility(true);
    this.#setThumbnailPlayBadge(true);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   */
  loadContent(focus = true) {
    if (this.getAttribute('data-media-loaded')) return;

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);

    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    this.refs.deferredMediaPlayButton?.classList.add('deferred-media__playing');

    if (content instanceof HTMLVideoElement && content.getAttribute('autoplay')) {
      // force autoplay for safari
      content.play();
    }

    // Attach ended listener to the newly loaded video element
    this.#attachVideoEndedListener();
  }

  /**
   * Toggle play/pause state of the media.
   * Reads the real <video> state when available so a click always does the
   * right thing even if the cached isPlaying flag has drifted out of sync
   * (e.g. after an auto-play/pause from a slideshow event).
   */
  toggleMedia() {
    const video = this.querySelector('video');
    const isPaused = video ? video.paused || video.ended : !this.isPlaying;

    if (isPaused) {
      this.playMedia();
    } else {
      this.pauseMedia();
    }
  }

  /**
   * Toggles mute/unmute state of the media.
   * For Shopify-hosted <video>, toggles the muted property directly.
   * For YouTube/Vimeo iframes, sends the appropriate postMessage command.
   */
  toggleMute() {
    this.isMuted = !this.isMuted;

    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      if (iframe.dataset.videoType === 'youtube') {
        iframe.contentWindow?.postMessage(
          JSON.stringify({
            event: 'command',
            func: this.isMuted ? 'mute' : 'unMute',
            args: '',
          }),
          '*'
        );
      } else {
        iframe.contentWindow?.postMessage(
          JSON.stringify({
            method: 'setVolume',
            value: this.isMuted ? 0 : 1,
          }),
          '*'
        );
      }
    } else {
      const video = this.querySelector('video');
      if (video) {
        video.muted = this.isMuted;
      }
    }

    // Toggle icon visibility: volume icon visible when unmuted, mute icon visible when muted
    const muteButton = this.refs.muteMediaButton;
    if (muteButton instanceof HTMLElement) {
      const volumeIcon = muteButton.querySelector('.icon-volume');
      const muteIcon = muteButton.querySelector('.icon-mute');
      if (volumeIcon) volumeIcon.classList.toggle('hidden', this.isMuted);
      if (muteIcon) muteIcon.classList.toggle('hidden', !this.isMuted);
    }
  }

  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      const video = this.querySelector('video');
      const playPromise = video?.play();
      // play() can reject when resuming an unmuted video without a fresh gesture.
      // Recover by muting and retrying so a click always resumes playback.
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          if (!video) return;
          video.muted = true;
          this.isMuted = true;
          video.play().catch(() => {});
        });
      }
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
    this.#updateMuteButtonVisibility(true);
    this.#setThumbnailPlayBadge(true);
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
    this.#updateMuteButtonVisibility(false);
    this.#setThumbnailPlayBadge(false);
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {
  #abortController = new AbortController();

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    const signal = this.#abortController.signal;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    element.addEventListener(
      'pointerdown',
      (/** @type {PointerEvent} */ event) => {
        pointerStartX = event.clientX;
        pointerStartY = event.clientY;
      },
      { signal }
    );

    element.addEventListener(
      'click',
      (/** @type {PointerEvent} */ event) => {
        const distanceX = Math.abs(event.clientX - pointerStartX);
        const distanceY = Math.abs(event.clientY - pointerStartY);
        const totalDistance = Math.sqrt(distanceX * distanceX + distanceY * distanceY);

        // Try to ensure that this is a tap, not a drag.
        if (totalDistance < 10) {
          // When the model is paused, it has its own button overlay for playing the model again.
          // If we're receiving a click event, it means the model is playing, all we can do is pause it.
          this.pauseMedia();
        }
      },
      { signal }
    );
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
